import { lazy, Suspense, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { api, asRecord, field } from './api';
import { AppShell } from './components/shell';
import { OnboardingGate } from './components/onboarding';
import { LoadingBlock } from './components/ui';
import { UnlockPage } from './pages/unlock';
import { MissionControlPage } from './pages/mission-control';

const RoadmapPage = lazy(() =>
  import('./pages/roadmap').then((module) => ({ default: module.RoadmapPage })),
);
const BriefingPage = lazy(() =>
  import('./pages/briefing').then((module) => ({ default: module.BriefingPage })),
);
const ScenariosPage = lazy(() =>
  import('./pages/scenarios').then((module) => ({ default: module.ScenariosPage })),
);
const ScenarioDetailPage = lazy(() =>
  import('./pages/scenarios').then((module) => ({ default: module.ScenarioDetailPage })),
);
const ResponsibilitiesPage = lazy(() =>
  import('./pages/responsibilities').then((module) => ({ default: module.ResponsibilitiesPage })),
);
const DecisionsPage = lazy(() =>
  import('./pages/decisions').then((module) => ({ default: module.DecisionsPage })),
);
const DecisionDetailPage = lazy(() =>
  import('./pages/decisions').then((module) => ({ default: module.DecisionDetailPage })),
);
const ModulesPage = lazy(() =>
  import('./pages/modules').then((module) => ({ default: module.ModulesPage })),
);
const ModuleDetailPage = lazy(() =>
  import('./pages/modules').then((module) => ({ default: module.ModuleDetailPage })),
);
const ResearchPage = lazy(() =>
  import('./pages/research').then((module) => ({ default: module.ResearchPage })),
);
const ClaimDetailPage = lazy(() =>
  import('./pages/research').then((module) => ({ default: module.ClaimDetailPage })),
);
const RecommendationDetailPage = lazy(() =>
  import('./pages/research').then((module) => ({ default: module.RecommendationDetailPage })),
);
const SourcesPage = lazy(() =>
  import('./pages/sources').then((module) => ({ default: module.SourcesPage })),
);
const ProfilePage = lazy(() =>
  import('./pages/profile').then((module) => ({ default: module.ProfilePage })),
);
const SettingsPage = lazy(() =>
  import('./pages/settings').then((module) => ({ default: module.SettingsPage })),
);

function AuthBoundary(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api<unknown>('/auth/session'),
    retry: false,
    staleTime: 60_000,
  });
  useEffect(() => {
    const expire = () => void navigate('/unlock', { replace: true });
    window.addEventListener('life:auth-required', expire);
    return () => window.removeEventListener('life:auth-required', expire);
  }, [navigate]);
  if (session.isLoading)
    return (
      <div className="app-loading">
        <span className="brand__mark">
          <span className="spinner spinner--light" />
        </span>
        <LoadingBlock label="Opening Akshay’s Atlas" />
      </div>
    );
  if (session.isError) return <Navigate to="/unlock" replace state={{ from: location.pathname }} />;
  const record = asRecord(session.data);
  const authenticated =
    field<boolean>(record, 'authenticated', 'isAuthenticated') ??
    Boolean(field(record, 'principal', 'session', 'id'));
  if (!authenticated) return <Navigate to="/unlock" replace state={{ from: location.pathname }} />;
  return (
    <OnboardingGate>
      <AppShell />
    </OnboardingGate>
  );
}

function LazyBoundary({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <Suspense fallback={<LoadingBlock label="Loading atlas view" />}>{children}</Suspense>;
}

export function App(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/unlock" element={<UnlockPage />} />
      <Route element={<AuthBoundary />}>
        <Route index element={<MissionControlPage />} />
        <Route
          path="briefing"
          element={
            <LazyBoundary>
              <BriefingPage />
            </LazyBoundary>
          }
        />
        <Route
          path="roadmap"
          element={
            <LazyBoundary>
              <RoadmapPage />
            </LazyBoundary>
          }
        />
        <Route
          path="scenarios"
          element={
            <LazyBoundary>
              <ScenariosPage />
            </LazyBoundary>
          }
        />
        <Route
          path="scenarios/:definitionId"
          element={
            <LazyBoundary>
              <ScenarioDetailPage />
            </LazyBoundary>
          }
        />
        <Route
          path="responsibilities"
          element={
            <LazyBoundary>
              <ResponsibilitiesPage />
            </LazyBoundary>
          }
        />
        <Route
          path="decisions"
          element={
            <LazyBoundary>
              <DecisionsPage />
            </LazyBoundary>
          }
        />
        <Route
          path="decisions/:id"
          element={
            <LazyBoundary>
              <DecisionDetailPage />
            </LazyBoundary>
          }
        />
        <Route
          path="modules"
          element={
            <LazyBoundary>
              <ModulesPage />
            </LazyBoundary>
          }
        />
        <Route
          path="modules/:slug"
          element={
            <LazyBoundary>
              <ModuleDetailPage />
            </LazyBoundary>
          }
        />
        <Route
          path="research"
          element={
            <LazyBoundary>
              <ResearchPage />
            </LazyBoundary>
          }
        />
        <Route
          path="research/claims/:id"
          element={
            <LazyBoundary>
              <ClaimDetailPage />
            </LazyBoundary>
          }
        />
        <Route
          path="research/recommendations/:id"
          element={
            <LazyBoundary>
              <RecommendationDetailPage />
            </LazyBoundary>
          }
        />
        <Route
          path="sources"
          element={
            <LazyBoundary>
              <SourcesPage />
            </LazyBoundary>
          }
        />
        <Route
          path="profile"
          element={
            <LazyBoundary>
              <ProfilePage />
            </LazyBoundary>
          }
        />
        <Route
          path="settings/:section"
          element={
            <LazyBoundary>
              <SettingsPage />
            </LazyBoundary>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
